import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function ContextMenu({ menu, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!menu) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose?.();
    }
    function onPointerDown(e) {
      const el = ref.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose?.();
    }
    function onScroll() {
      onClose?.();
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [menu, onClose]);

  useEffect(() => {
    if (!menu) {
      setPos(null);
      return;
    }
    const el = ref.current;
    if (!el) {
      setPos({ left: menu.x, top: menu.y });
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = 10;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const left = Math.max(pad, Math.min(menu.x, vw - r.width - pad));
    const top = Math.max(pad, Math.min(menu.y, vh - r.height - pad));
    setPos({ left, top });
  }, [menu]);

  if (!menu) return null;
  if (typeof document === 'undefined') return null;

  const items = Array.isArray(menu.items) ? menu.items : [];
  return createPortal(
    <div
      ref={ref}
      className="Menu Menu--fixed"
      style={{
        position: 'fixed',
        left: pos?.left ?? menu.x,
        top: pos?.top ?? menu.y,
        right: 'auto',
        bottom: 'auto',
      }}
      role="menu"
    >
      {items.map((it, idx) => {
        if (it?.type === 'sep') {
          return <div key={it.key || `sep_${idx}`} className="MenuSeparator" role="separator" />;
        }
        const disabled = Boolean(it?.disabled);
        return (
          <button
            key={it.key || `${idx}`}
            type="button"
            className={`MenuItem ${
              it.variant === 'danger' ? 'MenuItem--danger' : it.variant === 'good' ? 'MenuItem--good' : ''
            }`}
            role="menuitem"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              onClose?.();
              it.onClick?.();
            }}
          >
            <span className="ButtonInline" style={{ justifyContent: 'space-between', width: '100%' }}>
              <span className="ButtonInline" style={{ gap: 10 }}>
                {it.icon ? it.icon : null}
                <span>{it.label}</span>
              </span>
              {it.hint ? (
                <span className="Muted" style={{ fontSize: 12 }}>
                  {it.hint}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>,
    document.body
  );
}

